import React, { ReactNode, useContext, useCallback, useRef, useEffect, useState } from "react";
import memoize from "memoize-one";
import debug from "../debug";
import { Moment } from "moment";
import { Grid, AutoSizer, GridCellRenderer, Index } from "react-virtualized";
import { BoardState, TimelineEvent, Bucket } from "../tools/events";
import { Labels, Item } from "../types";
import BoardItem from "./BoardItem";
import EmptyBoardItem from "./EmptyBoardItem";
import ColumnHeader from "./ColumnHeader";
import ColumnFooter from "./ColumnFooter";
import { DataContext } from "./DataProvider";
import AttentionBox from "../common/AttentionBox";

import "./KanbanBoard.css";

const log = debug("KanbanBoard");

const COLUMN_WIDTH = 220;

interface Props {
    boardState: BoardState;
    itemsMovedFrom: { [itemId: string]: string | null };
    addedCount: { [labelId: string]: number };
    removedCount: { [labelId: string]: number };
    items: { [id: string]: Item };
    labels: Labels;
    timeUpdated: (newTime: Moment, isAnimation?: boolean, animateSingleItemId?: string) => void;
    nextEvent: () => boolean;

    showPrevNextEvents: boolean;
    showProgress: boolean;
    columnId: string;
    isPlaying: boolean;
    noEventsWithinFromTo: boolean;
}

type Column = { itemId: string | null; age: number }[];

const getColumns = (
    boardState: BoardState,
    removedCount: { [labelId: string]: number },
    labels: Labels
): { columns: Column[]; maxItemsInColumn: number } => {

    const columns: Column[] = new Array<Column>(labels.orderedIds.length);
    let maxItemsInColumn = 0;
    for (let i = 0; i < columns.length; i++) {
        const labelId = labels.orderedIds[i];
        const bucket: Bucket = boardState.buckets[labels.orderedIds[i]];

        // insert items from the bucket and sort them
        columns[i] = Object.keys(bucket).map((itemId) => ({ itemId, age: 1000 }));
        columns[i].sort((a, b) => bucket[a.itemId!] - bucket[b.itemId!]);

        // insert reserved spaces for items which have just moved out
        for (let r = 0; r < removedCount[labelId] || 0; r++) {
            columns[i].unshift({ itemId: null, age: 0 });
        }

        if (columns[i].length > maxItemsInColumn) {
            maxItemsInColumn = columns[i].length;
        }
    }

    return {
        columns,
        maxItemsInColumn
    };
};

// todo: supply column as well as it's known at the time user click
const findItemInColumns = memoize((itemId: string, columns: Column[]) => {
    for (let c = 0; c < columns.length; c++) {
        for (let r = 0; r < columns[c].length; r++) {
            if (columns[c][r].itemId === itemId) {
                return { columnIndex: c, rowIndex: r };
            }
        }
    }
    return { columnIndex: 0, rowIndex: 0 };
});

const findItemInColumn = memoize((itemId: string, column: Column) => {
    for (let r = 0; r < column.length; r++) {
        if (column[r].itemId === itemId) {
            return r;
        }
    }
    return 0;
});

const KanbanBoard: React.FC<Props> = function ({
    boardState,
    itemsMovedFrom,
    addedCount,
    removedCount,
    items,
    labels,
    timeUpdated,
    nextEvent,
    showPrevNextEvents,
    showProgress,
    columnId,
    isPlaying,
    noEventsWithinFromTo
}: Props) {
    const { time } = useContext(DataContext);
    const gridRef = useRef<Grid>(null);
    const jumpToRef = useRef<string | null>(null);

    // determine which direction item is coming from, for landing animation executed after move animation
    const [itemsMovedFromDirection, setItemsMovedFromDirection] = useState<{ [itemId: string]: string }>({});

    const { columns, maxItemsInColumn } = getColumns(boardState, removedCount, labels);

    const handleJumpToClick = useCallback(
        (e: TimelineEvent) => {
            timeUpdated(e.date, true, e.item);
            jumpToRef.current = e.item;
        },
        [timeUpdated]
    );

    const cellRenderer: GridCellRenderer = useCallback(
        ({ columnIndex, rowIndex, key, style }) => {
            // log(`cellRenderer: ${columnIndex}, ${rowIndex}`);
            const labelId = labels.orderedIds[columnIndex];
            const labelStyle = labels.labelsColors[labelId];
            let component: ReactNode = null;

            if (rowIndex === 0) {
                component = (
                    <ColumnHeader
                        labelId={labelId}
                        name={labels.names[labelId]}
                        color={labelStyle.color}
                        hasItems={columns[columnIndex].length > 0}
                    />
                );
            } else if (rowIndex <= columns[columnIndex].length) {
                const itemId = columns[columnIndex][rowIndex - 1].itemId;
                if (itemId === null) {
                    component = <EmptyBoardItem labelStyle={labelStyle} />;
                } else {
                    component = (
                        <BoardItem
                            itemId={itemId}
                            item={items[itemId]}
                            event={boardState.lastEvents[itemId]}
                            labels={labels}
                            movedFrom={itemsMovedFrom[itemId]}
                            moveDirection={itemsMovedFromDirection[itemId]}
                            // ease when playing animation to slow down before landing
                            // cubic-bezier when executing another bouncing animation after moving
                            moveStyle={{
                                animation: `move_item_${itemId} 0.5s ${
                                    isPlaying ? "ease" : "cubic-bezier(0.470, 0.000, 0.750, 0.750)"
                                } 0s 1 normal forwards`
                            }}
                            isPlaying={isPlaying}
                            onJumpToClick={handleJumpToClick}
                            showPrevNextEvents={showPrevNextEvents}
                            showProgress={showProgress}
                        />
                    );
                }
            } else if (rowIndex > 1 && rowIndex === columns[columnIndex].length + 1) {
                component = <ColumnFooter color={labelStyle.color} />;
            }
            return (
                <div key={key} style={style} className="board__cell">
                    {component}
                </div>
            );
        },
        [columns, showPrevNextEvents, itemsMovedFrom] // eslint-disable-line
    );

    const rowHeight = useCallback(
        ({ index }: Index): number => {
            if (index === 0) {
                return 40;
            }
            return 44 + (showPrevNextEvents ? 24 : 0) + (showProgress ? 24 : 0);
        },
        [showPrevNextEvents, showProgress]
    );

    useEffect(() => {
        if (gridRef.current) {
            log("Grid.forceUpdate");
            gridRef.current.recomputeGridSize();
        }
    }, [showPrevNextEvents, showProgress]);

    useEffect(() => {
        // handle jumping to speficied item
        if (jumpToRef.current !== null) {
            const { columnIndex, rowIndex } = findItemInColumns(jumpToRef.current, columns);
            log(`scrolling to item ${jumpToRef.current} in column: ${columnIndex}, row: ${rowIndex + 1}`);
            gridRef.current?.scrollToCell({ columnIndex, rowIndex: rowIndex + 1 }); // account for extra row for header
            jumpToRef.current = null;
        }
    }, [time]); // eslint-disable-line

    useEffect(() => {
        // handle animation of moving cards
        const grid: Grid | null = gridRef.current;
        const styles: HTMLStyleElement[] = [];
        const directions: { [itemId: string]: string } = {};

        if (grid) {
            // generate dynamic CSS and insert into document
            for (const itemId in itemsMovedFrom) {
                const fromLabelId = itemsMovedFrom[itemId];
                // check if item is moving from other label. if not, we just animate it dropping into place
                if (fromLabelId !== null) {
                    const fromColumnIndex = labels.order[fromLabelId];

                    const toLabelId = boardState.items[itemId][columnId];
                    const toColumnIndex = labels.order[toLabelId];
                    const toRowIndex = findItemInColumn(itemId, columns[toColumnIndex]);

                    const translateX = COLUMN_WIDTH * (toColumnIndex - fromColumnIndex);
                    const translateY = rowHeight({ index: 1 }) * toRowIndex;

                    const style = document.createElement("style");
                    const css = `@keyframes move_item_${itemId} {
                        0% {
                            transform: translate(${-translateX}px, ${-translateY}px) rotateZ(10deg);
                            visibility: visible;
                        }
                        100% {
                            visibility: visible;
                        }`;
                    style.type = "text/css";
                    style.appendChild(document.createTextNode(css));
                    document.getElementsByTagName("head")[0].appendChild(style);
                    styles.push(style);

                    directions[itemId] = toColumnIndex > fromColumnIndex ? "left" : "right";
                } else {
                    directions[itemId] = "top";
                }
            }
        }
        setItemsMovedFromDirection(directions);
        return (): void => {
            // remove dynamic CSS
            styles.forEach((style) => style.remove());
        };
    }, [itemsMovedFrom]); // eslint-disable-line

    return (
        <div className="board_container">
            <div className="board">
                <AutoSizer>
                    {({ width, height }): React.ReactElement => (
                        <Grid
                            width={width}
                            height={height}
                            rowHeight={rowHeight}
                            estimatedRowSize={rowHeight({ index: 1 })}
                            rowCount={maxItemsInColumn + 2}
                            cellRenderer={cellRenderer}
                            columnCount={labels.orderedIds.length}
                            columnWidth={COLUMN_WIDTH}
                            scrollToAlignment="center"
                            overscanColumnCount={3}
                            overscanRowCount={3}
                            ref={gridRef}
                        />
                    )}
                </AutoSizer>
            </div>

            {noEventsWithinFromTo ? (
                <div className="board_warning">
                    <AttentionBox title="Nothing to show">
                        <p>Nothing seems to have happened in selected timeframe for selected Status field.</p>
                        <p>
                            Try selecting different Status field or From Date and To Date in{" "}
                            <span className="strong">View Settings</span>.
                        </p>
                    </AttentionBox>
                </div>
            ) : maxItemsInColumn === 0 ? (
                <div className="board_warning">
                    <AttentionBox title="No cards on the board">
                        <p>
                            No cards to show, but don&apos;t worry.{" "}
                            <span className="strong">Cumulative Flow Diagram</span> at the top shows number of items
                            over time.
                        </p>
                        <p>
                            Move slider where there are items, press the Play button or{" "}
                            <span className="anchor_like_button" onClick={nextEvent}>
                                click here to move to the first event
                            </span>
                            .
                        </p>
                    </AttentionBox>
                </div>
            ) : null}
        </div>
    );
};

export default KanbanBoard;
